import { useEffect, useMemo, useRef, useState } from 'react'
import type { Audience, LeadState, SessionInfo, StepId } from '../types'
import { CONSENT_TEXT_REQUIRED, STEP_ORDER } from '../config'
import { ensureSession, logEvent, readUTMs, submitLead } from '../lib/api'
import { readSessionFromClient } from '../lib/session'
import { trackPageview, trackStart, trackStep, trackComplete, trackLead, trackSubmitLeadForm, trackAbandon, trackWelcomeEngaged, readFbParams, readTtParams, readGoogleAdsParams, getDeviceType, calculateLeadScore, setGa4UserId } from '../lib/tracking'
import { calculateSavings } from '../steps/stepDefinitions'
import { ThemeProvider } from '../themes/ThemeProvider'
import { audienceToTheme } from '../themes'
import { MessageList } from './MessageList'
import { useChatRunner, clearPersistedProgress } from './useChatRunner'
import { useResume } from './useResume'
import { IframeResize } from './IframeResize'
import { StepInput } from '../steps/StepInput'
import { ThankYou } from '../steps/ThankYou'

interface Props {
  embed?: boolean
  audience: Audience
}

export function Widget({ embed = false, audience }: Props) {
  // Force the audience prop into the initial SessionInfo so the first paint
  // matches the route — readSessionFromClient() may have read a stale cookie
  // from a prior visit to the other audience.
  const [session, setSession] = useState<SessionInfo>(() => ({
    ...readSessionFromClient(),
    audience,
  }))
  // sessionReady gates ChatRunner mount so we never POST /api/event for the
  // welcome view BEFORE /api/session has created the row in D1. Without this,
  // the first event(s) lose their FK insert silently — events table FK to
  // sessions, and the catch in /api/event swallows the error. Symptom: funnel
  // missing the welcome step (event is dropped, every later step shows up
  // because the session row finished creating in time for them).
  const [sessionReady, setSessionReady] = useState(false)
  const utm = useMemo(readUTMs, [])

  // Reconcile with server (sets canonical lf_session + lf_variant + lf_audience
  // cookies + records session row).
  useEffect(() => {
    let cancelled = false
    void ensureSession({ ...session, audience }, utm).then((s) => {
      if (cancelled) return
      setSession(s)
      setSessionReady(true)
      // Pin GA4 user_id til server-reconciled sessionId (ikke det client-
      // genererede UUID) så cross-session/cross-device user-stitching virker
      // og admin /api/admin/leads kan joines med GA4 BigQuery export på user_id.
      setGa4UserId(s.sessionId)
    })
    // GTM pageview — fires once per widget mount, NOT on /analytics (Widget doesn't render there).
    trackPageview(audience)
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience])

  const { resumable, decide, decision } = useResume(audience)

  return (
    <ThemeProvider theme={audienceToTheme[audience]}>
      <div className={`viewport-wrapper ${embed ? 'embed-view' : 'mobile-view'}`}>
        <div className="viewport-container">
          <div className="app-container">
            {decision === 'pending' && resumable ? (
              <>
                <ChatChrome currentStep="welcome" audience={audience} />
                <ResumePrompt
                  onContinue={() => decide('continue')}
                  onRestart={() => {
                    // Ryd persisted state inden re-mount så useChatRunner ikke
                    // arver den gamle (måske afsluttede) sessions data. ChatRunner
                    // re-mountes via key-prop'en når decision skifter, og dens
                    // useState(() => initialState ?? {}) starter dermed friskt.
                    clearPersistedProgress()
                    decide('restart')
                  }}
                />
              </>
            ) : !sessionReady ? (
              // Show chrome only while we wait for /api/session — keeps the user
              // looking at something rather than a blank canvas. Bot's first
              // typing animation already takes ~1s, so this gate is rarely
              // visible in practice.
              <ChatChrome currentStep="welcome" audience={audience} />
            ) : (
              <ChatRunner
                // Keying on `decision + sessionId` forces a fresh chat-runner once
                // the user makes their resume choice (or after a "Start forfra").
                key={`${decision}-${session.sessionId}`}
                session={session}
                audience={audience}
                utm={utm}
                initialStep={decision === 'continue' ? resumable?.step : undefined}
                initialState={decision === 'continue' ? resumable?.state : undefined}
              />
            )}
          </div>
        </div>
      </div>
      {embed && <IframeResize />}
    </ThemeProvider>
  )
}

function ChatChrome({ currentStep, audience }: { currentStep: StepId; audience: Audience }) {
  const order = STEP_ORDER[audience]
  const idx = order.indexOf(currentStep)
  // Hvis et tidligere gemt step ikke findes i den aktuelle audiences flow
  // (fx aeldre-bruger på `claims` der nu lander på unge-flowet), så viser vi
  // 0% i stedet for at crash'e med en negativ procent.
  const progress = idx >= 0 ? (idx / (order.length - 1)) * 100 : 0
  return (
    <>
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="chat-header">
        <div className="header-avatar">
          <img src="/fiona-avatar-final.png" alt="Fiona" />
        </div>
        <div className="header-text">
          <div className="header-name">Fiona</div>
          <div className="header-status">Online</div>
        </div>
      </div>
    </>
  )
}

interface ChatRunnerProps {
  session: SessionInfo
  audience: Audience
  utm: ReturnType<typeof readUTMs>
  initialStep?: StepId
  initialState?: LeadState
}

function ChatRunner({ session, audience, utm, initialStep, initialState }: ChatRunnerProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  // Wall-clock anchor for completion_seconds (lead-quality signal in GA4/Meta).
  const startTimeRef = useRef<number>(Date.now())
  const startedRef = useRef(false)
  const stepsCompletedRef = useRef(0)
  // Honeypot ref — feltet er position:absolute / aria-hidden så rigtige brugere
  // ikke ser eller udfylder det. Bots der parser DOM'en og mass-submit'er
  // formularer udfylder typisk alle inputs blindt → vi sender feltets værdi
  // i payloaden så backend kan dropp'e leadet stille uden at fortælle bot'en.
  const honeypotRef = useRef<HTMLInputElement>(null)

  const onComplete = async (state: LeadState) => {
    const completionSeconds = Math.round((Date.now() - startTimeRef.current) / 1000)
    // Fire GTM `complete` BEFORE the network call so the event survives even
    // if /api/lead is slow or fails — Meta still gets attribution.
    trackComplete(audience, state, completionSeconds)
    // Læs FB-attribution-params samme sted som Pixel-eventet bruger dem.
    // Forwardes til /api/lead → leads-tabellen → Zapier som top-level felter
    // så CRM kan sende Meta CAPI conversions tilbage med fbp/fbc/fbclid og
    // retargeting på fb_*_id.
    const fb = readFbParams()
    // TikTok-attribution-felter — parallel læsning med fb. Forwardes til /api/lead
    // → leads-tabellen → Zapier OG TikTok Events API (CAPI) i queue-worker.
    const tt = readTtParams()
    // Google Ads + Microsoft Bing click IDs — persisteres på leadet så Adversus
    // ved closed deal kan forwarde dem som part of Purchase-event til Google Ads
    // + Bing Offline Conversion Import (lukket-deal-attribution → Smart Bidding).
    const gads = readGoogleAdsParams()
    // Berigelsesfelter — server bruger disse til at bygge lead_payload med
    // svar/beregning/meta-nesteret struktur som Zapier-worker forwarder. Det
    // harmoniserer chat-payloaden med calculator's, så CRM ser samme contract.
    const sav = calculateSavings(state, audience)
    const monthlyVal = state.currentPriceVal ?? null
    const annualPremium = monthlyVal !== null ? monthlyVal * 12 : null
    const result = await submitLead({
      ...state,
      sessionId: session.sessionId,
      variant: session.variant,
      audience: session.audience,
      consentTextRequired: CONSENT_TEXT_REQUIRED,
      utm,
      _hp_email: honeypotRef.current?.value ?? '',
      fbp: fb.fbp,
      fbc: fb.fbc,
      fbclid: fb.fbclid,
      fb_campaign_id: fb.fb_campaign_id,
      fb_adset_id: fb.fb_adset_id,
      fb_ad_id: fb.fb_ad_id,
      fb_campaign_name: fb.fb_campaign_name,
      fb_adset_name: fb.fb_adset_name,
      fb_placement: fb.fb_placement,
      fb_platform: fb.fb_platform,
      ttclid: tt.ttclid,
      ttp: tt.ttp,
      tt_campaign_id: tt.tt_campaign_id,
      tt_adgroup_id: tt.tt_adgroup_id,
      tt_ad_id: tt.tt_ad_id,
      tt_creative_id: tt.tt_creative_id,
      tt_campaign_name: tt.tt_campaign_name,
      tt_adgroup_name: tt.tt_adgroup_name,
      tt_placement: tt.tt_placement,
      gclid: gads.gclid || undefined,
      msclkid: gads.msclkid || undefined,
      wbraid: gads.wbraid || undefined,
      gbraid: gads.gbraid || undefined,
      gclsrc: gads.gclsrc || undefined,
      completion_seconds: completionSeconds,
      device: getDeviceType(),
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      referrer: typeof document !== 'undefined' ? (document.referrer || undefined) : undefined,
      lead_score: calculateLeadScore(state),
      estimeret_aarlig_praemie: annualPremium ?? undefined,
      besparelse_low: sav.min,
      besparelse_high: sav.max,
    })
    // Only fire `lead` (Meta CompleteRegistration) if backend actually accepted —
    // prevents over-counting on 4xx (bad phone, dedup, honeypot).
    if (result.ok) {
      trackLead(audience, session.sessionId, state, completionSeconds)
      // submit_lead_form = bekræftet (post-POST) submission. GTM-tags for Google
      // Ads Enhanced Conversions, Bing UET form-event og GA4 "Udfyldt formular"
      // binder til denne i stedet for forsikringstjek_lead, så vi ikke over-
      // tæller konverteringer ved backend-hiccups.
      trackSubmitLeadForm(audience, session.sessionId, state)
      // Ryd persisted state efter accepted lead. Uden det ville et fremtidigt
      // besøg åbne resume-prompt'en for et allerede afsluttet flow, hvilket
      // (1) er forvirrende for brugeren og (2) potentielt re-injecter den
      // gamle leads payload hvis brugeren klikker "Fortsæt".
      clearPersistedProgress()
    }
    // Returnér result så useChatRunner kan vise fejl ved !result.ok i stedet
    // for at sende brugeren videre til thank-you uden at have ladet leadet
    // accepteret af backend.
    return result
  }

  const { state, currentStep, messages, inputVisible, isPosting, submitStep } = useChatRunner({
    audience,
    initialState,
    initialStep,
    onStepView: (step) => logEvent(session.sessionId, step, 'view'),
    onStepSubmit: (step, newState) => {
      logEvent(session.sessionId, step, 'submit')

      // GTM: first submit is the "start" of the funnel (calculator does the same).
      if (!startedRef.current) {
        startedRef.current = true
        trackStart(audience)
      }
      stepsCompletedRef.current += 1
      const idx = STEP_ORDER[audience].indexOf(step) + 1
      const answer = answerForStep(step, newState)
      trackStep(audience, idx, step, answer)
    },
    onComplete,
  })

  useEffect(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current
      if (!el) return
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
    })
  }, [messages, inputVisible])

  useEffect(() => {
    const handler = () => {
      if (currentStep === 'thankyou') return
      logEvent(session.sessionId, currentStep, 'abandon')
      // Only fire GTM abandon if user actually engaged (>0 steps) — otherwise
      // pageview-only bouncers would all show up as abandons and pollute reports.
      if (stepsCompletedRef.current > 0) {
        const seconds = Math.round((Date.now() - startTimeRef.current) / 1000)
        trackAbandon(audience, currentStep, stepsCompletedRef.current, seconds)
      }
    }
    window.addEventListener('pagehide', handler)
    return () => window.removeEventListener('pagehide', handler)
  }, [audience, currentStep, session.sessionId])

  // Welcome-CTA klik: track + advance til insurances. Kaldes fra inline
  // Start-knappen som rendereres som en chat-besked i MessageList.
  const onCta = (_label: string) => {
    if (currentStep !== 'welcome' || isPosting) return
    trackWelcomeEngaged(audience)
    void submitStep({})
  }

  return (
    <>
      <ChatChrome currentStep={currentStep} audience={audience} />
      <MessageList messages={messages} ref={scrollRef} onCta={onCta} ctaDisabled={isPosting} />

      {/* Honeypot — invisible to real users; bots fill it. Værdien sendes med
          submitLead-payloaden via honeypotRef → backend dropper leadet stille
          (returns {accepted:false}) hvis feltet er udfyldt. */}
      <input
        ref={honeypotRef}
        type="text"
        name="_hp_email"
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />

      {inputVisible && currentStep !== 'thankyou' && currentStep !== 'welcome' && (
        <StepInput step={currentStep} state={state} audience={audience} onSubmit={submitStep} />
      )}

      {currentStep === 'thankyou' && inputVisible && (
        <ThankYou />
      )}
    </>
  )
}

/**
 * Map a step to a PII-safe value to send to GTM as the `answer` field. We never
 * push phone, email, or full name to dataLayer — only category-level signals
 * that ad platforms can use for optimization.
 */
function answerForStep(step: StepId, state: LeadState): unknown {
  switch (step) {
    case 'welcome': return 'engaged'
    case 'insurances': return state.insurances ?? []
    case 'postnr': return state.postnr
    case 'age': return state.age
    case 'currentPrice': return state.currentPrice
    case 'km': return state.kmYear
    case 'claims': return state.claims
    case 'phone': return 'submitted'
    default: return null
  }
}

function ResumePrompt({ onContinue, onRestart }: { onContinue: () => void; onRestart: () => void }) {
  return (
    <div className="resume-overlay">
      <div className="resume-card">
        <h3>Vil du fortsætte hvor du slap?</h3>
        <p>Du var i gang med at få et tilbud. Vi har gemt dine svar — du behøver ikke starte forfra.</p>
        <div className="resume-actions">
          <button className="btn-send btn-secondary" onClick={onRestart}>Start forfra</button>
          <button className="btn-send" onClick={onContinue}>Fortsæt</button>
        </div>
      </div>
    </div>
  )
}
